import { PayloadAction } from '@reduxjs/toolkit';
import { all, call, fork, select, takeLatest, put, take, takeEvery } from 'redux-saga/effects';
import { RootState } from 'types';
import { IViewer, ViewerState, updatePlayerState, updateSceneState, updateViewerState } from 'core/slices/player';
import { EntityTypes } from 'constants/entities';
import { ActionType, IActionBasic } from 'types/domain/action';
import { selectAction } from 'core/selectors/product/action/action';
import { IUpdateState, restoreState, updateState } from 'core/actions/product/state';
import { moduleRequest } from 'core/services/product';
import { getRequest } from 'core/services/admin';
import { Mode, updateMode } from 'core/slices/session';
import { IActionFeed, selectActionFeed } from 'core/selectors/product/action/actionFeed';
import { LoadingState } from 'types/domain/base';
import { selectSceneAsset } from 'core/selectors/product/scene/sceneAsset';
import { IAsset } from 'types/app/asset';
import { unloadAsset } from 'core/slices/assets';

function* updateStateSaga(action: PayloadAction<Partial<IUpdateState>>) {
  if (!action.payload.id) return;

  switch (action.payload.type) {
    case EntityTypes.ACTION:
      yield loadActionScene(action.payload.id);
      break;
  }
}

function* updateViewerStateSaga(action: PayloadAction<IViewer>) {
  if (action.payload.state === ViewerState.RESTING) yield removeSceneIfNotRequired();
}

export function* loadActionScene(actionId) {
  //@ts-ignore
  let actionDAO: IActionBasic = yield select(s => selectAction(s, actionId));

  while (!actionDAO) {
    yield take([moduleRequest.fulfilled, getRequest.fulfilled]);
    //@ts-ignore
    actionDAO = yield select(s => selectAction(s, actionId));
  }

  const sceneId = yield select((state: RootState) => state.state[EntityTypes.SCENE]);
  if (sceneId !== actionDAO.sceneId) {
    const sceneAsset: IAsset = yield select(state => selectSceneAsset(state, undefined));
    if (sceneAsset) yield put(yield call(unloadAsset, sceneAsset.id));

    if (actionDAO.sceneId)
      yield all([
        put(yield call(updateState, { type: EntityTypes.SCENE, id: actionDAO.sceneId })),
        put(
          yield call(updateSceneState, {
            id: actionDAO.sceneId,
            load: LoadingState.LOADING,
            ended: false,
            started: false,
          }),
        ),
      ]);
  }
}

function* updateModeSaga(action: PayloadAction<Mode>) {
  if (action.payload === Mode.PREVIEW) {
    const actionDAO: IActionBasic = yield select(state => selectAction(state, undefined));
    const sceneId: number = yield select((state: RootState) => state.state[EntityTypes.SCENE]);
    if (actionDAO && actionDAO.sceneId && sceneId !== actionDAO.sceneId) {
      yield put(
        yield call(updatePlayerState, {
          paused: true,
          faded: actionDAO.type !== ActionType.INTERACTIVE,
          cuplrit: 'updateModeSaga',
        }),
      );
      yield put(yield call(updateState, { type: EntityTypes.SCENE, id: actionDAO.sceneId }));
    }
  }
}

function* incrementSaga() {
  yield removeSceneIfNotRequired();
}

export function* removeSceneIfNotRequired(prev = false) {
  const actionFeed: IActionFeed = yield select(selectActionFeed);
  const sceneId: number = yield select((state: RootState) => state.state[EntityTypes.SCENE]);

  const action: IActionBasic = yield select(state =>
    actionFeed.feed[actionFeed.index]?.actionId
      ? //@ts-ignore
        selectAction(state, actionFeed.feed[actionFeed.index].actionId)
      : null,
  );
  const id = actionFeed.feed[prev ? actionFeed.index - 1 : actionFeed.index + 1]?.actionId;
  const nextAction: IActionBasic = yield select((state: RootState) =>
    //@ts-ignore
    id ? selectAction(state, id) : null,
  );

  if (sceneId && !action?.sceneId && (!nextAction?.sceneId || nextAction?.sceneId !== action?.sceneId)) {
    yield put(yield call(updatePlayerState, { paused: true, faded: true, cuplrit: 'removeSceneIfNotRequired' }));
    yield put(yield call(updateState, { type: EntityTypes.SCENE, id: null }));
    //@ts-ignore
    const sceneAsset: IAsset = yield select(state => selectSceneAsset(state, sceneId));
    if (sceneAsset) yield put(yield call(unloadAsset, sceneAsset.id));
    yield put(
      yield call(updateSceneState, {
        id: sceneId,
        load: LoadingState.DEFAULT,
        ended: false,
        started: false,
      }),
    );
  }
}

function* restoreStateSaga(action: PayloadAction<IUpdateState[]>) {
  const update = action.payload.find(update => update.type === EntityTypes.ACTION);
  if (update) update.id ? yield loadActionScene(update.id) : yield removeSceneIfNotRequired();
}

function* watchUpdateMode() {
  yield takeLatest(updateMode, updateModeSaga);
}

function* watchUpdateState() {
  yield takeEvery(updateState.type, updateStateSaga);
}

function* watchRestoreState() {
  yield takeEvery(restoreState.type, restoreStateSaga);
}

function* watchViewerState() {
  yield takeEvery(updateViewerState, updateViewerStateSaga);
}

export function* sceneSaga() {
  yield all([fork(watchUpdateState), fork(watchUpdateMode), fork(watchRestoreState), fork(watchViewerState)]);
}
